"use client";

import React from "react";
import { motion } from "framer-motion";
import RoadmapTimeline, { IntegrationMilestone } from "@/components/ui/roadmap-timeline";
import FeaturesGrid, { IntegrationCategory, Integration } from "@/components/ui/features-grid";
import { Meteors } from "@/components/ui/meteors";
import { cn } from "@/lib/utils";
import { CTA } from "@/components/sections/cta";
import {
  ServerIcon,
  ShieldIcon,
  DatabaseIcon,
  BrainIcon,
  ChartBarIcon,
  CodeIcon,
  CloudIcon,
  LockIcon,
  GlobeIcon,
} from "lucide-react";

export default function RoadmapPage() {
  return (
    <div className="w-full min-h-screen overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <Meteors quantity={20} />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
        <motion.div
          className="text-center mb-12 md:mb-20"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Integration Roadmap
          </h1>
          <p className="max-w-3xl mx-auto text-lg text-gray-600 dark:text-gray-300">
            Explore our ambitious plans for integrating industry-leading technologies to
            provide a comprehensive platform for AI/ML and DevOps operations.
          </p>
        </motion.div>

        <div className="space-y-24">
          {/* Features Grid */}
          <section>
            <div className="text-center mb-10">
              <motion.h2
                className="text-3xl font-bold text-gray-900 dark:text-white mb-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                Current Integrations
              </motion.h2>
              <motion.p
                className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Discover the powerful tools and technologies already integrated into our platform
              </motion.p>
            </div>

            <FeaturesGrid 
              categories={integrationCategories} 
              integrations={currentIntegrations} 
            />
          </section>

          {/* Timeline */}
          <section>
            <div className="text-center mb-10">
              <motion.h2
                className="text-3xl font-bold text-gray-900 dark:text-white mb-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Development Timeline
              </motion.h2>
              <motion.p
                className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                Our journey of continuous innovation and platform enhancement
              </motion.p>
            </div>

            <RoadmapTimeline milestones={timelineMilestones} />
          </section>

          {/* Upcoming Integrations */}
          <section>
            <div className="text-center mb-10">
              <motion.h2
                className="text-3xl font-bold text-gray-900 dark:text-white mb-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                Upcoming Integrations
              </motion.h2>
              <motion.p
                className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                Preview the exciting new integrations coming soon to our platform
              </motion.p>
            </div>

            <FeaturesGrid 
              categories={integrationCategories} 
              integrations={upcomingIntegrations} 
            />
          </section>
        </div>
      </div>
      
      {/* CTA Section */}
      <section className="py-16">
        <CTA />
      </section>
    </div>
  );
}

// Data for the page
const integrationCategories: IntegrationCategory[] = [
  {
    id: "devops",
    name: "DevOps",
    icon: <ServerIcon className="w-4 h-4" />,
  },
  {
    id: "security",
    name: "Security",
    icon: <ShieldIcon className="w-4 h-4" />,
  },
  {
    id: "data",
    name: "Data",
    icon: <DatabaseIcon className="w-4 h-4" />,
  },
  {
    id: "ai-ml",
    name: "AI/ML",
    icon: <BrainIcon className="w-4 h-4" />,
  },
  {
    id: "monitoring",
    name: "Monitoring",
    icon: <ChartBarIcon className="w-4 h-4" />,
  },
];

const currentIntegrations: Integration[] = [
  {
    id: "sonarqube",
    title: "SonarQube",
    description: "Code quality and security analysis to identify bugs, vulnerabilities, and code smells in your codebase.",
    icon: <CodeIcon className="w-6 h-6" />,
    categoryId: "devops",
    thumbnail: "https://www.sonarqube.org/assets/logo-31ad3115b1b4b120f3d1efd63e6b13ac9f1f89437f0cf6881b4af0f084d51089.svg",
  },
  {
    id: "argocd",
    title: "Argo CD",
    description: "Declarative, GitOps continuous delivery tool for Kubernetes that automates the deployment of applications.",
    icon: <CloudIcon className="w-6 h-6" />,
    categoryId: "devops",
    thumbnail: "https://argo-cd.readthedocs.io/en/stable/assets/argo.png",
  },
  {
    id: "flux",
    title: "Flux CD",
    description: "GitOps-based continuous delivery solution for Kubernetes that reconciles cluster state with Git repositories.",
    icon: <CloudIcon className="w-6 h-6" />,
    categoryId: "devops",
    thumbnail: "https://fluxcd.io/img/flux-horizontal-color.png",
  },
  {
    id: "falco",
    title: "Falco",
    description: "Cloud-native runtime security with real-time threat detection for applications running in cloud environments.",
    icon: <ShieldIcon className="w-6 h-6" />,
    categoryId: "security",
    thumbnail: "https://falco.org/img/falco-logo.png",
  },
  {
    id: "thanos",
    title: "Thanos",
    description: "Highly available Prometheus setup with long-term storage capabilities for comprehensive monitoring.",
    icon: <ChartBarIcon className="w-6 h-6" />,
    categoryId: "monitoring",
    thumbnail: "https://raw.githubusercontent.com/cncf/artwork/master/projects/thanos/horizontal/color/thanos-horizontal-color.png",
  },
  {
    id: "loki",
    title: "Loki",
    description: "Log aggregation system inspired by Prometheus, optimized for logs from Kubernetes pods.",
    icon: <DatabaseIcon className="w-6 h-6" />,
    categoryId: "monitoring",
    thumbnail: "https://grafana.com/static/img/logos/logo-loki.svg",
  },
  {
    id: "minio",
    title: "MinIO",
    description: "High-performance object storage compatible with Amazon S3 API for cloud-native applications.",
    icon: <DatabaseIcon className="w-6 h-6" />,
    categoryId: "data",
    thumbnail: "https://min.io/resources/img/logo/minio-white.svg",
  },
  {
    id: "kubeflow",
    title: "Kubeflow",
    description: "Machine learning toolkit for Kubernetes that simplifies ML workflows with end-to-end orchestration.",
    icon: <BrainIcon className="w-6 h-6" />,
    categoryId: "ai-ml",
    thumbnail: "https://www.kubeflow.org/images/logo.svg",
  },
  {
    id: "seldon",
    title: "Seldon Core",
    description: "MLOps framework for deploying, managing, and optimizing machine learning models on Kubernetes.",
    icon: <BrainIcon className="w-6 h-6" />,
    categoryId: "ai-ml",
    thumbnail: "https://www.seldon.io/wp-content/uploads/2023/07/seldon_io-cover-1.png",
  },
];

const upcomingIntegrations: Integration[] = [
  {
    id: "kserve",
    title: "KServe",
    description: "Serverless inferencing on Kubernetes for machine learning models, supporting multiple frameworks.",
    icon: <BrainIcon className="w-6 h-6" />,
    categoryId: "ai-ml",
    thumbnail: "https://kserve.github.io/website/0.10/images/logos/kserve-logo-full.png",
  },
  {
    id: "feast",
    title: "Feast",
    description: "Feature store for machine learning that allows teams to manage and serve features to models in production.",
    icon: <DatabaseIcon className="w-6 h-6" />,
    categoryId: "ai-ml",
    thumbnail: "https://feast.dev/img/feast-logo-full.png",
  },
  {
    id: "mlflow",
    title: "MLflow",
    description: "Machine learning lifecycle platform for experiment tracking, reproducible runs, and model deployment.",
    icon: <BrainIcon className="w-6 h-6" />,
    categoryId: "ai-ml",
    thumbnail: "https://raw.githubusercontent.com/mlflow/mlflow/master/docs/source/_static/MLflow-logo-final-black.png",
  },
  {
    id: "h2o",
    title: "H2O.ai",
    description: "AI and machine learning platform that helps organizations to build and deploy machine learning models.",
    icon: <BrainIcon className="w-6 h-6" />,
    categoryId: "ai-ml",
    thumbnail: "https://www.h2o.ai/wp-content/uploads/2023/11/h2oai-logo.svg",
  },
  {
    id: "patternfly",
    title: "PatternFly",
    description: "Enterprise-grade design system for creating consistent and accessible user experiences.",
    icon: <GlobeIcon className="w-6 h-6" />,
    categoryId: "devops",
    thumbnail: "https://www.patternfly.org/assets/images/patternfly-logo-hero.png",
  },
  {
    id: "jupyterhub",
    title: "JupyterHub",
    description: "Multi-user Jupyter notebook environment for data science, machine learning, and analysis.",
    icon: <BrainIcon className="w-6 h-6" />,
    categoryId: "ai-ml",
    thumbnail: "https://jupyter.org/assets/homepage/main-logo.svg",
  },
];

const timelineMilestones: IntegrationMilestone[] = [
  {
    id: "milestone-1",
    title: "Initial DevOps Integrations",
    date: "Q1 2024",
    description: "Deployment of core DevOps tools including SonarQube, Argo CD, and Flux CD to establish continuous delivery pipelines.",
    category: "DevOps",
    status: "completed",
    icon: <ServerIcon className="w-4 h-4" />,
  },
  {
    id: "milestone-2",
    title: "Security Framework Implementation",
    date: "Q2 2024",
    description: "Integration of Falco for runtime security monitoring and threat detection across the platform.",
    category: "Security",
    status: "completed",
    icon: <ShieldIcon className="w-4 h-4" />,
  },
  {
    id: "milestone-3",
    title: "Monitoring Infrastructure",
    date: "Q2 2024",
    description: "Deployment of Thanos and Loki for comprehensive metric and log aggregation and analysis.",
    category: "Monitoring",
    status: "completed",
    icon: <ChartBarIcon className="w-4 h-4" />,
  },
  {
    id: "milestone-4",
    title: "Data Storage Solutions",
    date: "Q3 2024",
    description: "Implementation of MinIO object storage for scalable, high-performance data management.",
    category: "Data",
    status: "completed",
    icon: <DatabaseIcon className="w-4 h-4" />,
  },
  {
    id: "milestone-5",
    title: "First AI/ML Integrations",
    date: "Q4 2024",
    description: "Deployment of Kubeflow and Seldon Core to enable machine learning workflows and model serving.",
    category: "AI/ML",
    status: "completed",
    icon: <BrainIcon className="w-4 h-4" />,
  },
  {
    id: "milestone-6",
    title: "Advanced ML Operations",
    date: "Q1 2025",
    description: "Integration of KServe and Feast to enhance model serving capabilities and feature management.",
    category: "AI/ML",
    status: "in-progress",
    icon: <BrainIcon className="w-4 h-4" />,
  },
  {
    id: "milestone-7",
    title: "ML Lifecycle Management",
    date: "Q2 2025",
    description: "Addition of MLflow and H2O.ai to provide comprehensive machine learning lifecycle management.",
    category: "AI/ML",
    status: "planned",
    icon: <BrainIcon className="w-4 h-4" />,
  },
  {
    id: "milestone-8",
    title: "User Experience Enhancement",
    date: "Q3 2025",
    description: "Implementation of PatternFly and JupyterHub to improve user experience for both developers and data scientists.",
    category: "DevOps",
    status: "planned",
    icon: <GlobeIcon className="w-4 h-4" />,
  },
];
